import React, { useState, useEffect } from 'react';
import { RiFolderTransferLine, RiFolder3Line, RiRefreshLine } from 'react-icons/ri';
import { CasesApi } from '@/api';
import { formatFolderName } from '@/utils';
import Popover from '@/components/ui/Popover';

interface Folder {
  id: string;
  name: string;
  path: string;
  depth?: number;
  children?: Folder[];
}

interface DocumentMoveMenuProps {
  caseId: string;
  documentId: string;
  documentName: string;
  currentFolderId?: string | null;
  onMoveSuccess?: () => void;
  className?: string;
}

const DocumentMoveMenu: React.FC<DocumentMoveMenuProps> = ({
  caseId,
  documentId,
  documentName,
  currentFolderId,
  onMoveSuccess,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load folders when menu opens (always refresh to show newly created folders)
  useEffect(() => {
    if (isOpen) {
      loadFolders();
    }
  }, [isOpen]);

  const loadFolders = async () => {
    setLoading(true);
    try {
      // Include empty folders for move menu so users can move documents into them
      const response = await CasesApi.getFolderTree(caseId, true);
      if (response.success && response.tree) {
        setFolders(response.tree);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMove = async (
    targetFolderId: string | null,
    targetFolderName: string
  ) => {
    setMoving(true);
    setError(null);
    try {
      const response = await CasesApi.moveFile(
        caseId,
        documentId,
        targetFolderId
      );

      if (response.success) {
        console.log(`✅ Moved "${documentName}" to "${targetFolderName}"`);
        setIsOpen(false);
        if (onMoveSuccess) onMoveSuccess();
      } else {
        const errorMsg = 'Failed to move document. Please try again.';
        setError(errorMsg);
        console.error('Failed to move document:', response);
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Error moving document. Please try again.';
      setError(errorMsg);
      console.error('Error moving document:', error);
    } finally {
      setMoving(false);
    }
  };

  const renderFolder = (folder: Folder, depth: number = 0) => {
    const isCurrentFolder = folder.id === currentFolderId;
    const indent = depth * 12;

    return (
      <div key={folder.id}>
        <button
          type="button"
          onClick={() => handleMove(folder.id, folder.name)}
          disabled={isCurrentFolder || moving}
          className={`w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 ${
            isCurrentFolder || moving
              ? 'text-gray-400 cursor-not-allowed bg-gray-50'
              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
          }`}
          style={{ paddingLeft: `${12 + indent}px` }}
        >
          <RiFolder3Line className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{formatFolderName(folder.name)}</span>
          {isCurrentFolder && (
            <span className="ml-auto text-xs text-gray-400">(current)</span>
          )}
        </button>
        {folder.children &&
          folder.children.map(child => renderFolder(child, depth + 1))}
      </div>
    );
  };

  const popoverContent = (
    <div className="w-64 max-h-96 overflow-y-auto">
      <div className="py-1">
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <div className="text-xs font-semibold text-gray-700">
            Move to folder
          </div>
          <button
            type="button"
            onClick={loadFolders}
            disabled={loading}
            className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh folder list"
          >
            <RiRefreshLine className={`w-3.5 h-3.5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="px-3 py-2 text-xs text-red-600 bg-red-50 border-b border-red-200">
            {error}
          </div>
        )}

        {moving && (
          <div className="px-3 py-2 text-xs text-blue-600 bg-blue-50 border-b border-blue-200">
            Moving document...
          </div>
        )}

        {loading ? (
          <div className="px-3 py-4 text-center text-sm text-gray-500">
            Loading folders...
          </div>
        ) : folders.length === 0 ? (
          <div className="px-3 py-4">
            <div className="text-sm text-gray-500 mb-2">No folders yet</div>
          </div>
        ) : (
          <>
            {/* Folder list */}
            <div className="max-h-64 overflow-y-auto">
              {folders.map(folder => renderFolder(folder))}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      open={isOpen}
      onOpenChange={open => {
        if (!open) setIsOpen(false);
      }}
      side="bottom"
      align="end"
      className="p-0"
    >
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
        title="Move to folder"
      >
        <RiFolderTransferLine className="w-4 h-4 text-gray-600" />
      </button>
    </Popover>
  );
};

export default DocumentMoveMenu;
